import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from '../locales/en.json';

/**
 * i18next for the customer portal.
 *
 * EN is the source of truth and is bundled in the entry: it is the fallback for every other language, so
 * it has to be there before anything renders. The other seven locales (produced by the translation step,
 * 2026-08-19; pl 2026-09-02) are one chunk each, fetched only for the language the visitor reads — the
 * entry used to carry all eight, ~100 KB gzip of strings nobody on the page could read. The chunk is
 * loaded and registered before `initI18n` / `changeLanguage` resolve, so no render ever sees a language
 * whose strings are not there yet.
 */
export const SUPPORTED_LNGS = ['en', 'nl', 'de', 'fr', 'it', 'es', 'da', 'pl'] as const;
export type SupportedLng = (typeof SUPPORTED_LNGS)[number];

export const LOCALE_STORAGE_KEY = 'sf.locale';

/** Language names in their own language, for the switcher. */
export const LNG_LABELS: Record<SupportedLng, string> = {
  en: 'English',
  nl: 'Nederlands',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  da: 'Dansk',
  pl: 'Polski',
};

/**
 * The public marketing site, per locale. The portal links out to it in exactly one place — the
 * guarantee line above the order button — so the two commercial guarantees are stated in full on
 * the page that owns them (and bindingly in the terms) rather than paraphrased a third time here.
 */
export const MARKETING_HOME: Record<SupportedLng, string> = {
  en: 'https://solmiro.eu/',
  nl: 'https://solmiro.eu/nl/',
  de: 'https://solmiro.eu/de/',
  fr: 'https://solmiro.eu/fr/',
  it: 'https://solmiro.eu/it/',
  es: 'https://solmiro.eu/es/',
  da: 'https://solmiro.eu/da/',
  pl: 'https://solmiro.eu/pl/',
};

/** The guarantee band on the landing page, in the reader's own language. */
export function guaranteesUrl(lng?: string): string {
  const base = lng?.slice(0, 2) as SupportedLng | undefined;
  return `${(base && MARKETING_HOME[base]) || MARKETING_HOME.en}#guarantees`;
}

/** Every locale file is one `translation` namespace; `nav`, `legal` and the rest are sections inside it. */
const NAMESPACE = 'translation';

/** The two latin-ext faces Polish text needs (ł, ą, ę, ś, ć, ń, ź, ż); the @font-face rules are in @sf/ui styles.css. */
const POLISH_FONTS = [
  '/fonts/archivo-600-800-normal-latin-ext.woff2',
  '/fonts/source-sans-3-400-600-normal-latin-ext.woff2',
];

/**
 * One chunk per language but English (the keys are SUPPORTED_LNGS minus 'en', which the compiler checks).
 * Written out rather than as `import(`../locales/${lng}.json`)`: that form makes Vite glob every file in
 * the folder, en.json included, and warn that en.json — also imported statically above — cannot move
 * into a chunk of its own.
 */
const LOCALE_CHUNKS: Record<
  Exclude<SupportedLng, 'en'>,
  () => Promise<{ default: Record<string, unknown> }>
> = {
  nl: () => import('../locales/nl.json'),
  de: () => import('../locales/de.json'),
  fr: () => import('../locales/fr.json'),
  it: () => import('../locales/it.json'),
  es: () => import('../locales/es.json'),
  da: () => import('../locales/da.json'),
  pl: () => import('../locales/pl.json'),
};

function isChunkedLng(lng: string): lng is keyof typeof LOCALE_CHUNKS {
  return lng !== 'en' && (SUPPORTED_LNGS as readonly string[]).includes(lng);
}

/**
 * Fetches and registers the strings of `lng` unless they are there already. English is bundled, so it and
 * anything unsupported resolve at once. Resolves to whether a bundle was added.
 */
async function loadLocale(lng: string): Promise<boolean> {
  // `load: 'languageOnly'` below: 'pl-PL' reads the 'pl' file.
  const base = lng.split('-')[0];
  if (!isChunkedLng(base) || i18n.hasResourceBundle(base, NAMESPACE)) return false;
  const { default: strings } = await LOCALE_CHUNKS[base]();
  i18n.addResourceBundle(base, NAMESPACE, strings, true, true);
  return true;
}

/**
 * Polish is the one language whose first screen needs the latin-ext font files, and the browser only asks
 * for a unicode-range subset once a glyph from it has been laid out — about half a second after the text
 * appears on a slow phone. Asking as soon as the language is known starts both downloads alongside the
 * locale chunk instead. `crossorigin` is required on a font preload, as for the two in index.html.
 */
function preloadPolishFonts() {
  for (const href of POLISH_FONTS) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    link.href = href;
    document.head.appendChild(link);
  }
}

export async function initI18n() {
  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // English only: the visitor's own language is added below, once the detector has named it.
      resources: { en: { translation: en } },
      fallbackLng: 'en',
      supportedLngs: [...SUPPORTED_LNGS],
      nonExplicitSupportedLngs: true,
      load: 'languageOnly',
      interpolation: { escapeValue: false },
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        lookupQuerystring: 'lng',
        lookupLocalStorage: LOCALE_STORAGE_KEY,
        caches: ['localStorage'],
      },
      returnNull: false,
    });

  // The detector has picked the language (the querystring, the stored choice or the browser's), but
  // i18next has resolved it against English only. Fetch the rest and resolve once more, before the first
  // render. A chunk that cannot be fetched must not stop the app from booting: the visitor then reads
  // English, the fallback, and the next load tries again.
  const lng = i18n.language;
  if (lng.split('-')[0] === 'pl') preloadPolishFonts();
  try {
    if (await loadLocale(lng)) await i18n.changeLanguage(lng);
  } catch {
    /* English is already registered */
  }
}

let latestSwitch = 0;

/** Switches the UI language and remembers it (the API also stores it on the user profile). */
export async function changeLanguage(lng: string) {
  const thisSwitch = ++latestSwitch;
  // As in initI18n, a chunk that cannot be fetched (offline without the service worker, a tab left open
  // across a deploy, a blocker) must not make this reject: the callers — the switcher, the demo entry, the
  // profile save — cannot handle it. The choice is still made and remembered, the visitor reads English, the
  // fallback, until the strings arrive, and the next switch or load tries again.
  try {
    await loadLocale(lng);
  } catch {
    /* English is already registered */
  }
  // Two switches in quick succession: the chunks can arrive in either order, and the last one asked for
  // must be the one that sticks.
  if (thisSwitch !== latestSwitch) return;
  await i18n.changeLanguage(lng);
  document.documentElement.lang = i18n.resolvedLanguage ?? 'en';
}

export default i18n;
